import type {
  ContinuityObjectState,
  LatestStateDelta,
  LatestStateDeltaChange,
  Ledger,
  PhysicalContinuityObligation,
} from '../types';
import { formatLedgerRecordForPrompt } from './authoritativeLedgerMerge.service';

function stableId(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    if (typeof record.objectId === 'string') return record.objectId;
    if (typeof record.name === 'string') return record.name;
    if (typeof record.id === 'string') return record.id;
  }
  return fallback;
}

function summarizeRecordChange(
  id: string,
  prior: unknown,
  next: unknown,
  changeType: LatestStateDeltaChange['changeType'],
): LatestStateDeltaChange {
  return {
    id,
    changeType,
    summary: `${changeType}: ${formatLedgerRecordForPrompt(next || prior)}`,
  };
}

function diffRecordMap(
  prior: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
  label: string,
): LatestStateDeltaChange[] {
  const changes: LatestStateDeltaChange[] = [];
  const priorMap = prior || {};
  const nextMap = next || {};
  for (const [key, value] of Object.entries(nextMap)) {
    if (!(key in priorMap)) {
      changes.push(summarizeRecordChange(key, null, value, 'introduced'));
    } else if (JSON.stringify(priorMap[key]) !== JSON.stringify(value)) {
      changes.push(summarizeRecordChange(key, priorMap[key], value, 'updated'));
    }
  }
  for (const key of Object.keys(priorMap)) {
    if (!(key in nextMap)) {
      changes.push({
        id: key,
        changeType: 'removed',
        summary: `${label} removed: ${formatLedgerRecordForPrompt(priorMap[key])}`,
      });
    }
  }
  return changes;
}

function diffStringArrays(
  prior: unknown[] | undefined,
  next: unknown[] | undefined,
  label: string,
): LatestStateDeltaChange[] {
  const priorSet = new Set((prior || []).map((entry) => formatLedgerRecordForPrompt(entry)));
  const nextSet = new Set((next || []).map((entry) => formatLedgerRecordForPrompt(entry)));
  const changes: LatestStateDeltaChange[] = [];
  for (const entry of nextSet) {
    if (!priorSet.has(entry)) {
      changes.push({ id: entry, changeType: 'introduced', summary: `${label} gained: ${entry}` });
    }
  }
  for (const entry of priorSet) {
    if (!nextSet.has(entry)) {
      changes.push({ id: entry, changeType: 'resolved', summary: `${label} resolved/removed: ${entry}` });
    }
  }
  return changes;
}

function diffPhysicalObjects(
  prior: ContinuityObjectState[] | undefined,
  next: ContinuityObjectState[] | undefined,
): LatestStateDeltaChange[] {
  const priorMap = new Map((prior || []).map((object) => [object.objectId, object]));
  const nextMap = new Map((next || []).map((object) => [object.objectId, object]));
  const changes: LatestStateDeltaChange[] = [];
  for (const [id, object] of nextMap) {
    const before = priorMap.get(id);
    if (!before) {
      changes.push({
        id,
        changeType: 'introduced',
        summary: `Physical object introduced: ${object.canonicalName} (${object.operationalStatus})`,
      });
      continue;
    }
    if (JSON.stringify(before) !== JSON.stringify(object)) {
      changes.push({
        id,
        changeType: 'updated',
        summary: `Physical object updated: ${object.canonicalName} (${before.operationalStatus} → ${object.operationalStatus})`,
      });
    }
  }
  return changes;
}

function diffObligations(
  prior: PhysicalContinuityObligation[] | undefined,
  next: PhysicalContinuityObligation[] | undefined,
): { opened: LatestStateDeltaChange[]; resolved: LatestStateDeltaChange[] } {
  const priorMap = new Map((prior || []).map((entry) => [entry.obligationId, entry]));
  const nextMap = new Map((next || []).map((entry) => [entry.obligationId, entry]));
  const opened: LatestStateDeltaChange[] = [];
  const resolved: LatestStateDeltaChange[] = [];
  for (const [id, obligation] of nextMap) {
    const before = priorMap.get(id);
    if (!before) {
      opened.push({
        id,
        changeType: 'introduced',
        summary: obligation.description,
        sourceChapter: obligation.originatingChapter,
      });
      continue;
    }
    if (before.status !== 'OPEN' && obligation.status === 'OPEN') {
      opened.push({ id, changeType: 'introduced', summary: obligation.description });
    }
    if (before.status === 'OPEN' && obligation.status !== 'OPEN') {
      resolved.push({ id, changeType: 'resolved', summary: obligation.description });
    }
  }
  for (const [id, obligation] of priorMap) {
    if (obligation.status === 'OPEN' && !nextMap.has(id)) {
      resolved.push({ id, changeType: 'resolved', summary: obligation.description });
    }
  }
  return { opened, resolved };
}

function ingestArchivistDelta(
  archivistDelta: unknown,
  delta: LatestStateDelta,
): void {
  if (!archivistDelta || typeof archivistDelta !== 'object') return;
  const raw = archivistDelta as Record<string, unknown>;
  const appendStrings = (target: string[], value: unknown) => {
    if (!Array.isArray(value)) return;
    for (const entry of value) {
      if (typeof entry === 'string' && entry.trim()) target.push(entry.trim());
    }
  };
  appendStrings(delta.consequences, raw.unresolvedConsequences);
  appendStrings(delta.consequences, raw.new_unresolved_consequences);
  appendStrings(delta.consequences, raw.gained);
  appendStrings(delta.consequences, raw.revealed);
  appendStrings(delta.consequences, raw.threatsEscalated);
  if (Array.isArray(raw.relationshipsChanged)) {
    for (const entry of raw.relationshipsChanged) {
      if (typeof entry === 'string') {
        delta.relationshipChanges.push({
          id: stableId(entry, entry),
          changeType: 'updated',
          summary: entry,
        });
      }
    }
  }
}

export function deriveLatestStateDelta(params: {
  priorLedger: Ledger;
  mergedLedger: Ledger;
  chapterNumber: number;
  chapterRevisionId?: string;
  archivistDelta?: unknown;
}): LatestStateDelta {
  const { priorLedger, mergedLedger, chapterNumber, chapterRevisionId, archivistDelta } = params;
  const obligationDiff = diffObligations(
    priorLedger.physical_continuity_obligations,
    mergedLedger.physical_continuity_obligations,
  );

  const delta: LatestStateDelta = {
    chapterNumber,
    chapterRevisionId,
    characterChanges: diffRecordMap(priorLedger.characters, mergedLedger.characters, 'Character'),
    relationshipChanges: diffRecordMap(priorLedger.relationships, mergedLedger.relationships, 'Relationship'),
    locationChanges: diffRecordMap(priorLedger.locations, mergedLedger.locations, 'Location'),
    inventoryChanges: diffStringArrays(priorLedger.inventory, mergedLedger.inventory, 'Inventory'),
    physicalObjectChanges: diffPhysicalObjects(
      priorLedger.continuity_objects,
      mergedLedger.continuity_objects,
    ),
    knowledgeChanges: diffStringArrays(
      priorLedger.resolved_plot_points,
      mergedLedger.resolved_plot_points,
      'Knowledge',
    ),
    plotThreadChanges: [
      ...diffStringArrays(priorLedger.plot_points, mergedLedger.plot_points, 'Plot thread'),
    ],
    obligationsOpened: obligationDiff.opened,
    obligationsResolved: obligationDiff.resolved,
    consequences: [],
    generatedAt: new Date().toISOString(),
  };

  ingestArchivistDelta(archivistDelta, delta);

  if (!delta.consequences.length) {
    delta.consequences = [
      ...delta.plotThreadChanges.map((change) => change.summary),
      ...delta.obligationsOpened.map((change) => change.summary),
      ...delta.physicalObjectChanges.map((change) => change.summary),
      ...delta.characterChanges.filter((change) => change.changeType === 'updated').map((change) => change.summary),
    ].filter(Boolean);
  }

  return delta;
}

export function buildPriorChapterConsequences(delta?: LatestStateDelta): string[] {
  if (!delta) return [];
  return [
    ...delta.consequences,
    ...delta.obligationsOpened.map((entry) => `OPEN OBLIGATION: ${entry.summary}`),
    ...delta.physicalObjectChanges.map((entry) => entry.summary),
    ...delta.characterChanges.map((entry) => entry.summary),
    ...delta.inventoryChanges.map((entry) => entry.summary),
  ].filter(Boolean);
}

export function latestStateDeltaToNarrativeStateDelta(
  delta?: LatestStateDelta,
): import('../types').NarrativeStateDelta | undefined {
  if (!delta) return undefined;
  return {
    fact_changes: [
      ...delta.characterChanges.map((entry) => entry.summary),
      ...delta.locationChanges.map((entry) => entry.summary),
      ...delta.inventoryChanges.map((entry) => entry.summary),
      ...delta.physicalObjectChanges.map((entry) => entry.summary),
    ],
    emotional_shifts: delta.relationshipChanges.map((entry) => entry.summary),
    new_unresolved_consequences: delta.consequences,
    resolved_consequences: delta.obligationsResolved.map((entry) => entry.summary),
  };
}
